import React, { useEffect, useMemo, useState } from "react";
import { Link } from "react-router-dom";
import { useAccounts } from "../lib/account";
import { moneyFor } from "../lib/currency";
import { api } from "../lib/api";
import EventTimer from "../components/EventTimer";
import LiveChart from "../components/LiveChart";
import TvAdvancedChart from "../components/TvAdvancedChart";
import SymbolSearch from "../components/SymbolSearch";
import useSeo from "../lib/useSeo";
import {
  CurrencyBtc, CurrencyEth, Coin, Drop, GasPump,
  ArrowUp, ArrowDown, ArrowRight, Info, Warning,
} from "@phosphor-icons/react";

// The `/charts` page is our permanently-free chart tab (visible to every
// signed-up user with or without a subscription) so we can retain the
// TradingView "no-paywall" Advanced Charts license. Uses the same LiveChart
// component + `/api/prop-sim/quote|candles` datafeed as the Prop Challenge
// terminal so the price shown always matches the paper-fill price.
const SYMBOLS = [
  { id: "BTCUSD",       label: "BTC · USDT",       icon: CurrencyBtc, accent: "#f7931a", match: /BTC/i,               group: "Crypto" },
  { id: "ETHUSD",       label: "ETH · USDT",       icon: CurrencyEth, accent: "#627eea", match: /ETH/i,               group: "Crypto" },
  { id: "XAUUSD",       label: "Gold · XAU",       icon: Coin,        accent: "#f5c518", match: /XAU|GOLD/i,          group: "Metals" },
  { id: "XAGUSD",       label: "Silver · XAG",     icon: Drop,        accent: "#c0c0c0", match: /XAG|SILVER/i,        group: "Metals" },
  { id: "USOIL",        label: "Crude · WTI",      icon: GasPump,     accent: "#e57373", match: /OIL|CRUDE|WTI|USOIL/i, group: "Energy" },
  // MCX (Feb 2026 re-add) — Dhan v2 auto-rolling FUTCOM contracts,
  // prices in ₹. Backend picks the next-month expiry live from the
  // scrip master CSV so contracts roll over automatically.
  { id: "MCX_GOLD",     label: "Gold · MCX ₹",     icon: Coin,        accent: "#facc15", match: /GOLD/i,              group: "MCX", mcx: true },
  { id: "MCX_SILVER",   label: "Silver · MCX ₹",   icon: Drop,        accent: "#e5e7eb", match: /SILVER/i,            group: "MCX", mcx: true },
  { id: "MCX_CRUDEOIL", label: "Crude · MCX ₹",    icon: GasPump,     accent: "#fb923c", match: /CRUDE|OIL/i,         group: "MCX", mcx: true },
];

const INTERVALS = [
  { id: "1",   label: "1m" },
  { id: "5",   label: "5m" },
  { id: "15",  label: "15m" },
  { id: "60",  label: "1H" },
  { id: "240", label: "4H" },
  { id: "D",   label: "1D" },
  { id: "W",   label: "1W" },
];

const TIME_FILTERS = [
  { id: "today",   label: "Today" },
  { id: "yday",    label: "Yesterday" },
  { id: "7d",      label: "7d" },
  { id: "30d",     label: "30d" },
  { id: "all",     label: "All" },
];

// Returns true if `iso` (YYYY-MM-DD... string) falls inside the selected window.
function inTimeWindow(iso, filterId) {
  if (!filterId || filterId === "all") return true;
  const d = (iso || "").slice(0, 10);
  if (!d) return false;
  const today = new Date();
  const todayIso = today.toISOString().slice(0, 10);
  const yday = new Date(today.getTime() - 86400000).toISOString().slice(0, 10);
  if (filterId === "today") return d === todayIso;
  if (filterId === "yday")  return d === yday;
  if (filterId === "7d") {
    const cutoff = new Date(today.getTime() - 7 * 86400000).toISOString().slice(0, 10);
    return d >= cutoff;
  }
  if (filterId === "30d") {
    const cutoff = new Date(today.getTime() - 30 * 86400000).toISOString().slice(0, 10);
    return d >= cutoff;
  }
  return true;
}

// "Your trades on {symbol}" side panel. Filters `trades` by the symbol regex
// and renders them newest-first as pinned rows — the pragmatic substitute for
// on-chart markers (TradingView's free widget doesn't expose a marker API).
function TradesOnSymbol({ trades, symbol, account }) {
  const [timeFilter, setTimeFilter] = useState("all");

  const filtered = useMemo(() => {
    return (trades || [])
      .filter(t => symbol.match.test(t.symbol || ""))
      .filter(t => inTimeWindow(t.entry_time, timeFilter))
      .sort((a, b) => (b.entry_time || "").localeCompare(a.entry_time || ""))
      // Show only the 10 most-recent trades so the side panel stays
      // scannable at a glance (per user request Feb 2026). Header stats
      // still reflect the FULL filtered window (see totalPnl/winRate).
      .slice(0, 10);
  }, [trades, symbol, timeFilter]);

  const totalPnl = filtered.reduce((s, t) => s + (t.pnl || 0), 0);
  const wins = filtered.filter(t => (t.pnl || 0) > 0).length;
  const winRate = filtered.length ? Math.round((wins / filtered.length) * 100) : 0;

  return (
    <aside className="rounded-2xl border border-border bg-card overflow-hidden flex flex-col"
      data-testid="chart-trades-panel">
      <div className="p-4 border-b border-border">
        <div className="flex items-center gap-2">
          <symbol.icon size={16} weight="fill" style={{ color: symbol.accent }} />
          <p className="text-[10px] tracking-[0.2em] uppercase text-muted-foreground">Your trades</p>
        </div>
        <h3 className="font-display text-lg font-semibold mt-1">{symbol.label}</h3>

        {/* Time-window filter */}
        <div className="mt-3 flex items-center gap-1 flex-wrap" data-testid="panel-time-filter">
          {TIME_FILTERS.map(f => (
            <button key={f.id} onClick={() => setTimeFilter(f.id)}
              data-testid={`panel-time-${f.id}`}
              className={`px-2 py-0.5 rounded-full text-[10px] font-mono tracking-wider transition-colors ${
                timeFilter === f.id
                  ? "bg-primary text-primary-foreground font-bold"
                  : "bg-secondary/60 text-muted-foreground hover:text-foreground"
              }`}>
              {f.label}
            </button>
          ))}
        </div>

        {filtered.length > 0 ? (
          <div className="mt-3 grid grid-cols-3 gap-2 text-xs">
            <div>
              <p className="text-[9px] uppercase tracking-wider text-muted-foreground">Trades</p>
              <p className="font-mono-num font-bold mt-0.5">{filtered.length}</p>
            </div>
            <div>
              <p className="text-[9px] uppercase tracking-wider text-muted-foreground">Win</p>
              <p className="font-mono-num font-bold mt-0.5">{winRate}%</p>
            </div>
            <div>
              <p className="text-[9px] uppercase tracking-wider text-muted-foreground">Net P&L</p>
              <p className={`font-mono-num font-bold mt-0.5 ${totalPnl >= 0 ? "text-emerald-400" : "text-red-500"}`}>
                {moneyFor(account, totalPnl, { sign: true, short: true })}
              </p>
            </div>
          </div>
        ) : (
          <p className="text-xs text-muted-foreground mt-3">
            No {symbol.id} trades yet. Add one and it'll show up here.
          </p>
        )}
      </div>

      <div className="flex-1 overflow-y-auto min-h-0 hide-scroll" data-testid="chart-trades-list">
        {filtered.length === 0 ? (
          <p className="text-xs text-muted-foreground p-6 text-center">
            {timeFilter === "all"
              ? `Journal a ${symbol.id} trade to see it pinned here alongside the live chart.`
              : `No ${symbol.id} trades in this window. Try a wider range above.`}
          </p>
        ) : (
          filtered.map(t => {
            const isLong = t.direction === "long";
            const positive = (t.pnl || 0) >= 0;
            return (
              <Link key={t.id} to={`/trades/${t.id}`}
                data-testid={`chart-trade-row-${t.id}`}
                className="block px-4 py-3 border-b border-border/60 hover:bg-secondary/40 transition-colors group">
                <div className="flex items-start gap-3">
                  <div className={`w-6 h-6 rounded-full flex items-center justify-center shrink-0 ${
                    isLong ? "bg-emerald-500/20 text-emerald-400" : "bg-red-500/20 text-red-500"
                  }`}>
                    {isLong ? <ArrowUp size={12} weight="bold" /> : <ArrowDown size={12} weight="bold" />}
                  </div>
                  <div className="flex-1 min-w-0">
                    <div className="flex items-baseline justify-between gap-2">
                      <p className="text-xs font-mono font-semibold truncate">{t.symbol}</p>
                      <p className={`font-mono-num text-xs font-bold ${positive ? "text-emerald-400" : "text-red-500"}`}>
                        {moneyFor(account, t.pnl || 0, { sign: true, short: true })}
                      </p>
                    </div>
                    <p className="text-[10px] text-muted-foreground mt-1 font-mono">
                      {(t.entry_time || "").slice(0, 16).replace("T", " ")}
                    </p>
                    <div className="flex items-center gap-3 mt-1.5 text-[10px] text-muted-foreground font-mono">
                      <span>ENT <span className="text-foreground font-semibold">{Number(t.entry_price).toFixed(2)}</span></span>
                      <span>EXT <span className="text-foreground font-semibold">{Number(t.exit_price).toFixed(2)}</span></span>
                      <span>Q <span className="text-foreground font-semibold">{t.quantity}</span></span>
                    </div>
                  </div>
                  <ArrowRight size={12} className="text-muted-foreground group-hover:text-primary mt-1 shrink-0 transition-colors" />
                </div>
              </Link>
            );
          })
        )}
      </div>

      <div className="p-3 border-t border-border bg-secondary/30">
        <div className="flex items-start gap-2 text-[10px] text-muted-foreground">
          <Info size={12} className="shrink-0 mt-0.5" />
          <p>
            TradingView's free widget doesn't expose a drawing API, so trade pins live
            here beside the chart instead of on the candles.
          </p>
        </div>
      </div>
    </aside>
  );
}

export default function Charts() {
  useSeo({
    title: "Live Trading Charts · Free Forever · Tradelog by Yash Khajania",
    description: "Free forever live trading charts with drawing tools, bar countdown, and paper-fill matching your journal. BTC, ETH, Gold, Silver, Crude Oil, MCX Gold/Silver/Crude — no signup, no paywall.",
    canonical: "/charts",
  });
  const { activeId, activeAccount } = useAccounts();
  const [symbol, setSymbol] = useState(SYMBOLS[0]);
  // Symbols with no reliable sub-5m tape (MCX Dhan feed, Binance USDT-M
  // metals + oil perp). Chart opens at 5m by default and stays >=5m.
  const CAP5M_IDS = React.useMemo(() => new Set(["MCX_GOLD","MCX_SILVER","MCX_CRUDEOIL","XAUUSD","XAGUSD","USOIL"]), []);
  const _cap5m = CAP5M_IDS.has(symbol.id);
  const [interval, setInterval] = useState(_cap5m ? "5" : "1");
  // When the user picks a 5m-min symbol, snap the timeframe up if it was
  // on 1m or 3m — the base UDF datafeed would otherwise fetch a resolution
  // the /symbols endpoint no longer advertises and TV renders nothing.
  useEffect(() => {
    if (_cap5m && (interval === "1" || interval === "3")) setInterval("5");
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [symbol.id]);
  const [trades, setTrades] = useState([]);
  const [dhanHealth, setDhanHealth] = useState(null); // {configured, active_contracts}

  // Load the account's trades once (and whenever the account changes) so the
  // side panel can filter locally as the user switches symbol tabs — snappy.
  useEffect(() => {
    if (!activeId) return;
    api.get(`/trades?account_id=${activeId}`).then(({ data }) => setTrades(data || []));
  }, [activeId]);

  // Poll Dhan health so we know whether MCX tabs will work. Cheap — cached
  // in the backend, hit once on mount and once per symbol switch to MCX.
  useEffect(() => {
    api.get("/dhan/health").then(({ data }) => setDhanHealth(data)).catch(() => setDhanHealth({ configured: false }));
  }, []);

  const showMcxBanner = symbol.mcx && dhanHealth && !dhanHealth.configured;
  const activeContract = symbol.mcx && dhanHealth?.active_contracts
    ? dhanHealth.active_contracts[symbol.id.replace("MCX_", "")]
    : null;

  return (
    <div className="p-4 sm:p-6 lg:p-10 max-w-[1800px] mx-auto">
      <div>
        <p className="text-[10px] sm:text-[11px] tracking-[0.25em] sm:tracking-[0.3em] uppercase text-muted-foreground">Live Market</p>
        <h1 className="font-display text-3xl sm:text-4xl lg:text-5xl font-bold tracking-tight mt-2">
          Charts
        </h1>
        <p className="text-xs sm:text-sm text-muted-foreground mt-1">
          Real-time price feed with drawing tools, bar countdown, and your journal trades pinned in the panel beside the chart.
          Always free — no subscription needed.
        </p>
      </div>

      {/* All-in-one symbol search — hits /api/tv-udf/search across our 16
          tradable symbols + 500-symbol static universe + TwelveData 100k+
          long tail. Selecting a result jumps the chart to that ticker. */}
      <div className="mt-6" data-testid="chart-symbol-search-row">
        <SymbolSearch onPick={(r) => {
          setSymbol({
            id: r.symbol,
            label: r.symbol,
            icon: SYMBOLS[0].icon,
            accent: "#38bdf8",
            match: new RegExp(r.symbol, "i"),
            group: (r.type || "").replace(/^./, (c) => c.toUpperCase()),
          });
        }} />
      </div>

      {/* Symbol tabs — quick chips for the most-traded instruments */}
      <div className="mt-4 flex items-center gap-2 flex-wrap" data-testid="chart-symbol-tabs">
        {SYMBOLS.map((s) => {
          const Icon = s.icon;
          const active = symbol.id === s.id;
          return (
            <button key={s.id} onClick={() => setSymbol(s)}
              data-testid={`chart-tab-${s.id}`}
              className={`flex items-center gap-1.5 sm:gap-2 px-3 py-1.5 sm:px-4 sm:py-2 rounded-full text-xs sm:text-sm font-medium transition-all border ${
                active
                  ? "border-transparent text-black"
                  : "border-border bg-secondary/40 hover:border-primary/40 text-foreground"
              }`}
              style={active ? { background: s.accent } : {}}>
              <Icon size={14} weight={active ? "fill" : "regular"} />
              {s.label}
              {s.mcx && (
                <span className={`ml-1 text-[9px] px-1.5 py-0.5 rounded font-mono tracking-wider ${
                  active ? "bg-black/20 text-black" : "bg-orange-500/20 text-orange-300"
                }`}>MCX</span>
              )}
            </button>
          );
        })}
      </div>

      {/* MCX / Dhan configuration banner — appears only when user picks an
          MCX symbol AND Dhan API keys aren't set yet. Shows the current
          resolved contract when configured. */}
      {showMcxBanner && (
        <div className="mt-4 rounded-xl border border-orange-500/40 bg-orange-500/5 p-4"
          data-testid="dhan-config-banner">
          <div className="flex items-start gap-3">
            <Warning size={18} weight="fill" className="text-orange-400 shrink-0 mt-0.5" />
            <div className="flex-1">
              <p className="text-sm font-semibold text-orange-200">Dhan API not connected</p>
              <p className="text-xs text-muted-foreground mt-1">
                MCX Gold, Silver and Crude Oil live charts route through Dhan's DhanHQ v2 data API.
                Head to <Link to="/admin" className="text-orange-300 underline">/admin → Dhan MCX Data API</Link> and paste your access token + client ID.
                No Republish needed — swaps live. Dhan Data API subscription (~₹499/mo) required.
              </p>
            </div>
          </div>
        </div>
      )}

      {symbol.mcx && activeContract && null}

      {/* Timeframe row (style/candle-type is baked into LiveChart) */}
      <div className="mt-4 flex items-center gap-4 flex-wrap">
        <div className="flex items-center gap-1 rounded-full bg-secondary/50 p-1" data-testid="chart-interval-row">
          {INTERVALS.map((i) => (
            <button key={i.id} onClick={() => setInterval(i.id)}
              data-testid={`chart-interval-${i.id}`}
              className={`px-3 py-1 rounded-full text-xs font-mono tracking-wider transition-colors ${
                interval === i.id ? "bg-primary text-primary-foreground font-bold" : "text-muted-foreground hover:text-foreground"
              }`}>
              {i.label}
            </button>
          ))}
        </div>
      </div>

      {/* Upcoming economic events — countdown strip */}
      <div className="mt-5">
        <EventTimer hours={48} limit={5} impact="all" />
      </div>

      {/* Chart + trades panel */}
      <div className="mt-4 sm:mt-6 grid grid-cols-1 xl:grid-cols-[minmax(0,1fr)_360px] gap-4 sm:gap-6"
        style={{ minHeight: "480px" }}>
        <div className="rounded-2xl border border-border bg-card overflow-hidden h-[70vh] sm:h-[calc(100vh-340px)] sm:min-h-[600px]" data-testid="tv-chart-surface">
          <TvAdvancedChart symbol={symbol.id} interval={interval} height="100%" liveMode={true} onIntervalChange={setInterval} />
        </div>
        <TradesOnSymbol trades={trades} symbol={symbol} account={activeAccount} />
      </div>

      <p className="text-[10px] text-muted-foreground mt-3 font-mono uppercase tracking-[0.2em]">
        {symbol.label} · {INTERVALS.find(i => i.id === interval)?.label} · Live · Same feed as the order panel
      </p>
    </div>
  );
}
